import { useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { depositFromSamples, type EquitySample } from '../lib/equityHistory';

export type MonitorPeriod = '15m' | '1h' | '6h' | '24h' | 'all';

type Labels = {
  equity: string;
  pnl: string;
  empty: string;
  deposit?: string;
  period15m?: string;
  period1h?: string;
  period6h?: string;
  period24h?: string;
  periodAll?: string;
};

type Props = {
  samples: EquitySample[];
  /** Compact strip vs taller monitoring panel */
  tall?: boolean;
  labels?: Labels;
  period?: MonitorPeriod;
  onPeriodChange?: (p: MonitorPeriod) => void;
};

const PERIOD_MS: Record<MonitorPeriod, number> = {
  '15m': 15 * 60_000,
  '1h': 60 * 60_000,
  '6h': 6 * 60 * 60_000,
  '24h': 24 * 60 * 60_000,
  all: Number.POSITIVE_INFINITY,
};

const fmt = (n: number, digits = 2) => {
  if (!Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${n >= 0 && n < 1e-9 ? '' : sign}${n.toFixed(digits)}`;
};

const fmtAbs = (n: number, digits = 2) => (Number.isFinite(n) ? n.toFixed(digits) : '—');

const fmtTime = (t: number) => {
  try {
    return new Date(t).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return '';
  }
};

const fmtAxisTime = (t: number, spanMs: number) => {
  try {
    if (spanMs >= 6 * 60 * 60_000) {
      return new Date(t).toLocaleString(undefined, {
        month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
      });
    }
    return new Date(t).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

type SeriesKey = 'balance' | 'realized';

export default function MonitorSparklines({
  samples,
  tall = false,
  labels = { equity: 'Balance', pnl: 'Realized', empty: 'Collecting…' },
  period: periodProp,
  onPeriodChange,
}: Props) {
  const [showBalance, setShowBalance] = useState(true);
  const [showRealized, setShowRealized] = useState(true);
  const [periodLocal, setPeriodLocal] = useState<MonitorPeriod>('1h');
  const period = periodProp ?? periodLocal;
  const setPeriod = (p: MonitorPeriod) => {
    onPeriodChange?.(p);
    if (periodProp == null) setPeriodLocal(p);
  };
  const [hover, setHover] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const windowed = useMemo(() => {
    if (!samples.length) return samples;
    const ms = PERIOD_MS[period];
    if (!Number.isFinite(ms)) return samples;
    const cutoff = Date.now() - ms;
    const filtered = samples.filter((s) => s.t >= cutoff);
    return filtered.length >= 2 ? filtered : samples.slice(-Math.min(samples.length, 8));
  }, [samples, period]);

  const deposit = depositFromSamples(windowed.length ? windowed : samples);
  const balance = useMemo(() => windowed.map((s) => s.equity), [windowed]);
  const realized = useMemo(
    () => windowed.map((s) => (Number.isFinite(s.realized) ? Number(s.realized) : 0)),
    [windowed],
  );

  const w = tall ? 640 : 520;
  const h = tall ? 188 : 128;
  const padL = 52;
  const padR = 48;
  const padT = 12;
  const padB = 26;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;

  const activeKeys: SeriesKey[] = [];
  if (showBalance) activeKeys.push('balance');
  if (showRealized) activeKeys.push('realized');

  const spanMs = windowed.length >= 2
    ? windowed[windowed.length - 1].t - windowed[0].t
    : 0;

  const { balMin, balMax, pnlMin, pnlMax, balPath, pnlPath, xAt, balY, pnlY } = useMemo(() => {
    const empty = {
      balMin: 0, balMax: 1, pnlMin: -1, pnlMax: 1,
      balPath: '', pnlPath: '',
      xAt: (_i: number) => padL,
      balY: (_v: number) => padT + innerH / 2,
      pnlY: (_v: number) => padT + innerH / 2,
    };
    if (windowed.length < 2) return empty;

    let bMin = Infinity;
    let bMax = -Infinity;
    for (const v of balance) {
      if (!Number.isFinite(v)) continue;
      bMin = Math.min(bMin, v);
      bMax = Math.max(bMax, v);
    }
    if (deposit != null && Number.isFinite(deposit)) {
      bMin = Math.min(bMin, deposit);
      bMax = Math.max(bMax, deposit);
    }
    if (!Number.isFinite(bMin) || !Number.isFinite(bMax)) {
      bMin = 0;
      bMax = 1;
    }
    if (bMax - bMin < 1e-6) {
      bMax = bMin + 1;
      bMin -= 1;
    }
    // Pad 6% so labels don't sit on the line
    const bPad = (bMax - bMin) * 0.06;
    bMin -= bPad;
    bMax += bPad;

    let pMin = Infinity;
    let pMax = -Infinity;
    for (const v of realized) {
      if (!Number.isFinite(v)) continue;
      pMin = Math.min(pMin, v);
      pMax = Math.max(pMax, v);
    }
    if (!Number.isFinite(pMin) || !Number.isFinite(pMax)) {
      pMin = -1;
      pMax = 1;
    }
    pMin = Math.min(pMin, 0);
    pMax = Math.max(pMax, 0);
    if (pMax - pMin < 1e-6) {
      pMax = 1;
      pMin = -1;
    }
    const pPad = (pMax - pMin) * 0.08;
    pMin -= pPad;
    pMax += pPad;

    const x = (i: number) => padL + (i / (windowed.length - 1)) * innerW;
    const yBal = (v: number) => padT + (1 - (v - bMin) / (bMax - bMin)) * innerH;
    const yPnl = (v: number) => padT + (1 - (v - pMin) / (pMax - pMin)) * innerH;

    const pathOf = (vals: number[], yFn: (v: number) => number) => vals.map((v, i) => {
      const px = x(i);
      const py = yFn(v);
      return `${i === 0 ? 'M' : 'L'}${px.toFixed(1)} ${py.toFixed(1)}`;
    }).join(' ');

    return {
      balMin: bMin,
      balMax: bMax,
      pnlMin: pMin,
      pnlMax: pMax,
      balPath: pathOf(balance, yBal),
      pnlPath: pathOf(realized, yPnl),
      xAt: x,
      balY: yBal,
      pnlY: yPnl,
    };
  }, [windowed, balance, realized, deposit, innerW, innerH]);

  const lastBal = balance.length ? balance[balance.length - 1] : null;
  const lastPnl = realized.length ? realized[realized.length - 1] : null;
  const hi = hover != null && hover >= 0 && hover < windowed.length ? hover : null;
  const tip = hi != null ? windowed[hi] : null;

  const onMove = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (windowed.length < 2 || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * w;
    const i = Math.round(((px - padL) / innerW) * (windowed.length - 1));
    setHover(Math.max(0, Math.min(windowed.length - 1, i)));
  };

  const ticks = 4;
  const balTicks = Array.from({ length: ticks }, (_, i) => balMin + ((balMax - balMin) * i) / (ticks - 1));
  const pnlTicks = Array.from({ length: ticks }, (_, i) => pnlMin + ((pnlMax - pnlMin) * i) / (ticks - 1));
  const timeTicks = windowed.length >= 2
    ? [0, Math.floor((windowed.length - 1) / 3), Math.floor((2 * (windowed.length - 1)) / 3), windowed.length - 1]
    : [];

  const periodBtns: Array<{ id: MonitorPeriod; label: string }> = [
    { id: '15m', label: labels.period15m || '15m' },
    { id: '1h', label: labels.period1h || '1h' },
    { id: '6h', label: labels.period6h || '6h' },
    { id: '24h', label: labels.period24h || '24h' },
    { id: 'all', label: labels.periodAll || 'All' },
  ];

  const wrap: CSSProperties = {
    marginTop: tall ? 8 : 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  };

  const periodBar = (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {periodBtns.map((b) => {
        const on = period === b.id;
        return (
          <button
            key={b.id}
            type="button"
            onClick={() => setPeriod(b.id)}
            style={{
              padding: '3px 8px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
              border: `1px solid ${on ? 'var(--gold)' : 'var(--line)'}`,
              color: on ? 'var(--gold)' : 'var(--text)',
              background: on ? 'rgba(201,162,39,0.12)' : 'var(--panel)',
              fontWeight: on ? 700 : 500,
            }}
          >
            {b.label}
          </button>
        );
      })}
    </div>
  );

  if (windowed.length < 2) {
    return (
      <div style={wrap} title="0 samples">
        {periodBar}
        <SeriesToggles
          showBalance={showBalance}
          showRealized={showRealized}
          onBalance={setShowBalance}
          onRealized={setShowRealized}
          labels={labels}
          lastBal={null}
          lastPnl={null}
          deposit={deposit}
        />
        <div style={{
          height: h, borderRadius: 8, border: '1px solid var(--line)',
          background: 'var(--panel-2)', display: 'grid', placeItems: 'center',
          fontSize: 11, color: 'var(--muted)',
        }}>
          {labels.empty}
        </div>
      </div>
    );
  }

  const zeroY = pnlY(0);

  return (
    <div style={wrap} title={`${windowed.length} samples`}>
      {periodBar}
      <SeriesToggles
        showBalance={showBalance}
        showRealized={showRealized}
        onBalance={setShowBalance}
        onRealized={setShowRealized}
        labels={labels}
        lastBal={lastBal}
        lastPnl={lastPnl}
        deposit={deposit}
      />

      <div style={{
        borderRadius: 8, border: '1px solid var(--line)',
        background: 'var(--panel-2)', overflow: 'hidden', position: 'relative',
      }}>
        <svg
          ref={svgRef}
          width="100%"
          height={h}
          viewBox={`0 0 ${w} ${h}`}
          preserveAspectRatio="none"
          style={{ display: 'block', cursor: 'crosshair', touchAction: 'none' }}
          onPointerMove={onMove}
          onPointerLeave={() => setHover(null)}
        >
          {/* Plot frame */}
          <rect
            x={padL}
            y={padT}
            width={innerW}
            height={innerH}
            fill="none"
            stroke="var(--line)"
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
          />

          {balTicks.map((v, i) => {
            const y = balY(v);
            return (
              <line
                key={`g${i}`}
                x1={padL}
                x2={w - padR}
                y1={y}
                y2={y}
                stroke="var(--line)"
                strokeOpacity={0.55}
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
            );
          })}

          {showBalance && deposit != null ? (
            <line
              x1={padL}
              x2={w - padR}
              y1={balY(deposit)}
              y2={balY(deposit)}
              stroke="var(--gold)"
              strokeOpacity={0.55}
              strokeWidth={1}
              strokeDasharray="4 3"
              vectorEffect="non-scaling-stroke"
            />
          ) : null}

          {showRealized ? (
            <line
              x1={padL}
              x2={w - padR}
              y1={zeroY}
              y2={zeroY}
              stroke="var(--muted)"
              strokeOpacity={0.45}
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
          ) : null}

          {showBalance && balPath ? (
            <path d={balPath} fill="none" stroke="var(--gold)" strokeWidth="2" vectorEffect="non-scaling-stroke" />
          ) : null}
          {showRealized && pnlPath ? (
            <path
              d={pnlPath}
              fill="none"
              stroke={(lastPnl ?? 0) >= 0 ? 'var(--long)' : 'var(--short)'}
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
            />
          ) : null}

          {/* Left axis: balance */}
          {showBalance ? balTicks.map((v, i) => (
            <text
              key={`bl${i}`}
              x={padL - 5}
              y={balY(v) + 3.5}
              textAnchor="end"
              fill="var(--gold)"
              fontSize="10"
              fontFamily="var(--mono)"
              fontWeight={600}
            >
              {fmtAbs(v, Math.abs(v) >= 100 ? 1 : 2)}
            </text>
          )) : null}

          {/* Right axis: realized */}
          {showRealized ? pnlTicks.map((v, i) => (
            <text
              key={`pl${i}`}
              x={w - padR + 5}
              y={pnlY(v) + 3.5}
              textAnchor="start"
              fill={(v >= 0 ? 'var(--long)' : 'var(--short)')}
              fontSize="10"
              fontFamily="var(--mono)"
              fontWeight={600}
            >
              {fmt(v, 2)}
            </text>
          )) : null}

          {/* Time axis */}
          {timeTicks.map((i) => (
            <g key={`t${i}`}>
              <line
                x1={xAt(i)}
                x2={xAt(i)}
                y1={h - padB}
                y2={h - padB + 4}
                stroke="var(--muted)"
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
              <text
                x={xAt(i)}
                y={h - 7}
                textAnchor={i === 0 ? 'start' : i === windowed.length - 1 ? 'end' : 'middle'}
                fill="var(--muted)"
                fontSize="10"
                fontFamily="var(--mono)"
              >
                {fmtAxisTime(windowed[i].t, spanMs)}
              </text>
            </g>
          ))}

          {hi != null ? (
            <>
              <line
                x1={xAt(hi)}
                x2={xAt(hi)}
                y1={padT}
                y2={h - padB}
                stroke="var(--text)"
                strokeOpacity={0.35}
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
              {showBalance ? (
                <circle cx={xAt(hi)} cy={balY(balance[hi])} r={3.5} fill="var(--gold)" />
              ) : null}
              {showRealized ? (
                <circle
                  cx={xAt(hi)}
                  cy={pnlY(realized[hi])}
                  r={3.5}
                  fill={realized[hi] >= 0 ? 'var(--long)' : 'var(--short)'}
                />
              ) : null}
            </>
          ) : null}
        </svg>

        {tip ? (
          <div style={{
            position: 'absolute', left: 8, top: 6, pointerEvents: 'none',
            fontSize: 10, fontFamily: 'var(--mono)', lineHeight: 1.35,
            background: 'var(--overlay-chip)', border: '1px solid var(--line)',
            borderRadius: 6, padding: '4px 7px', color: 'var(--text)',
          }}>
            <div style={{ color: 'var(--muted)' }}>{fmtTime(tip.t)}</div>
            {showBalance ? (
              <div style={{ color: 'var(--gold)' }}>{labels.equity} {fmtAbs(tip.equity)}</div>
            ) : null}
            {showRealized ? (
              <div style={{ color: (tip.realized ?? 0) >= 0 ? 'var(--long)' : 'var(--short)' }}>
                {labels.pnl} {fmt(Number(tip.realized) || 0)}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {activeKeys.length === 0 ? (
        <div style={{ fontSize: 10, color: 'var(--muted)' }}>—</div>
      ) : null}
    </div>
  );
}

function SeriesToggles(props: {
  showBalance: boolean;
  showRealized: boolean;
  onBalance: (v: boolean) => void;
  onRealized: (v: boolean) => void;
  labels: Labels;
  lastBal: number | null;
  lastPnl: number | null;
  deposit: number | null;
}) {
  const deltaBal = props.lastBal != null && props.deposit != null
    ? props.lastBal - props.deposit
    : null;
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
      fontSize: 11, color: 'var(--muted)',
    }}>
      <label style={{ display: 'flex', gap: 5, alignItems: 'center', cursor: 'pointer', color: 'var(--gold)' }}>
        <input
          type="checkbox"
          checked={props.showBalance}
          onChange={(e) => props.onBalance(e.target.checked)}
        />
        <span>
          {props.labels.equity}
          {props.lastBal != null ? (
            <span style={{ fontFamily: 'var(--mono)', marginLeft: 4, color: 'var(--text)' }}>
              {fmtAbs(props.lastBal)}
              {deltaBal != null ? ` · ${fmt(deltaBal)}` : ''}
            </span>
          ) : null}
        </span>
      </label>
      <label style={{
        display: 'flex', gap: 5, alignItems: 'center', cursor: 'pointer',
        color: (props.lastPnl ?? 0) >= 0 ? 'var(--long)' : 'var(--short)',
      }}>
        <input
          type="checkbox"
          checked={props.showRealized}
          onChange={(e) => props.onRealized(e.target.checked)}
        />
        <span>
          {props.labels.pnl}
          {props.lastPnl != null ? (
            <span style={{ fontFamily: 'var(--mono)', marginLeft: 4 }}>{fmt(props.lastPnl)}</span>
          ) : null}
        </span>
      </label>
      {props.deposit != null ? (
        <span style={{ fontFamily: 'var(--mono)', marginLeft: 'auto', color: 'var(--text)' }}>
          {props.labels.deposit || 'Deposit'} {fmtAbs(props.deposit)}
        </span>
      ) : null}
    </div>
  );
}
